import {
    IconImageWithContext,
    ImageProcessingState,
    ImageWarnings,
    isUsable,
    UploadStateSummary,
} from "@nova/constants"
import { getFileNameFromImageId, UploadState } from "@nova/slice-multifile-upload"
import { createSelector } from "@reduxjs/toolkit"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectIconById } from "features/entity-caching/icons/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { get, memoize } from "nodash"
import { getIconImageS3Key } from "panels/content/side-panel/upload-flow/constants"
import {
    IconImageState,
    selectUploadsForSet as selectIconImageUploadsForSet,
} from "plugins/Toolbox/Icons/IconSlice"

export const selectRawIconImagesByIconId = memoize((id: string) =>
    createSelector([selectIconById(id)], (icon) => icon?.images || [])
)

export const selectIconImagesByIconId = memoize((id: string) =>
    createSelector(
        [selectRawIconImagesByIconId(id), selectActiveWorkspaceId(), selectActivatedFireId],
        (images, orgId, projectId) => {
            if (!orgId || !projectId) {
                return []
            }

            return images.map((image) => {
                return {
                    ...image,
                    s3Key: getIconImageS3Key({ orgId, projectId, flightId: id, fileId: image.id }),
                    flightId: id,
                    fileName: getFileNameFromImageId(image.id),
                    state: ImageProcessingState.IngestDone,
                }
            }) as IconImageWithContext[]
        }
    )
)

export const selectIconImageByIdAndIconId = memoize(
    (iconId: string, imageId: string) =>
        createSelector([selectIconImagesByIconId(iconId)], (images) => {
            return images.find((image) => image.id === imageId)
        }),
    (iconId, imageId) => `${iconId}|${imageId}`
)

export const selectIconImagesAndUploadsByIconId = memoize((id: string) =>
    createSelector(
        [selectIconImagesByIconId(id), selectIconImageUploadsForSet(id)],
        (images, uploads) => {
            const res = [] as (UploadState<IconImageState> | IconImageWithContext)[]
            const set = new Set()
            images.forEach((image) => {
                set.add(image.id)
                res.push(image)
            })
            uploads?.forEach((upload) => {
                if (!set.has(upload.id)) res.push(upload)
            })
            return res
        }
    )
)

export const selectSummaryFromIconUploads = memoize((id: string) =>
    createSelector([selectIconImagesAndUploadsByIconId(id)], (uploads) => {
        const summary: UploadStateSummary = {
            stateCount: {} as Record<ImageProcessingState, number>,
            uploadCount: uploads?.length || 0,
            errorCount: 0,
            warnings: {} as ImageWarnings,
        }
        const seenIds: Record<string, boolean> = {}
        uploads?.forEach((upload) => {
            if (seenIds[upload.id]) return
            seenIds[upload.id] = true
            const usable = isUsable(upload)
            if (!usable) return summary.errorCount++
            summary.stateCount[usable.state] = ++summary.stateCount[usable.state] || 1
        })
        return summary
    })
)

export const selectIsIconUploading = memoize((id: string) =>
    createSelector([selectSummaryFromIconUploads(id)], (summary) => {
        return (
            !!summary.stateCount?.[ImageProcessingState.PendingUpload] ||
            !!summary.stateCount?.[ImageProcessingState.Uploading]
        )
    })
)

export const selectIconUploadSize = memoize((id: string) =>
    createSelector([selectIconImagesAndUploadsByIconId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const size = get(upload, "size", 0)
            totalSize += size
        })
        return totalSize
    })
)

export const selectIconFinishedUploadSize = memoize((id: string) =>
    createSelector([selectIconImagesAndUploadsByIconId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const state = get(upload, "state")
            const size = get(upload, "size", 0)
            if (state !== ImageProcessingState.UploadDone) return
            totalSize += size
        })
        return totalSize
    })
)
